import startCase from 'lodash/startCase';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { BasicPerson } from 'types';

import {
  fromApiFilter,
  toApiFilter,
} from 'views/Widgets/Filters/AdvancedSpecializedFilter/model';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import { ApiFilterItem, TGroup } from 'views/Widgets/Filters/types';

export enum CampaignReportStatus {
  PUBLISHED = 'P',
  UNPUBLISHED = 'U',
  PARTIALLY_PUBLISHED = 'PARTIALLY_PUBLISHED',
}

export enum CampaignReportItemStatus {
  PUBLISHED = 'P',
  UNPUBLISHED = 'U',
}

export enum CampaignReportType {
  ENGAGEMENT_SURVEY = 'E',
}

export enum CampaignReportSubType {
  ES_ADMINS = 'E',
  USER_DEFINED = 'U',
}

export type CampaignReportESSliceType = 'DEMOGRAPHIC' | 'LEADERS' | 'CUSTOM';

export type CampaignReportConfigES = {
  slice_type?: CampaignReportESSliceType;
  slice_id?: string;
};

export type ApiCampaignReportExtraInfo = {
  custom_name?: string;
  custom_description?: string;
  filters?: {
    include: ApiFilterItem[];
    exclude: ApiFilterItem[];
  };
  status: CampaignReportStatus;
};

export type ApiCampaignReport = {
  id: number;
  type: CampaignReportType;
  sub_type: CampaignReportSubType;
  status: CampaignReportStatus;
  config: CampaignReportConfigES;
  extra_info: ApiCampaignReportExtraInfo;
  editable: boolean;
};

export type ApiCampaignReportItem = {
  id: number;
  key: string;
  name: string;
  owners: BasicPerson[];
  viewers: BasicPerson[];
  people_notified: BasicPerson[];
  count: number;
  status: CampaignReportItemStatus;
  config: {
    filters?: ApiGroup;
  };
};

export type CampaignReportItem = {
  name: string;
  viewers: BasicPerson[];
  owners: BasicPerson[];
  status: CampaignReportItemStatus;
  filters?: TGroup;
  count: number;
};

export type ApiResponseReport = {
  description: ApiCampaignReport;
  items: ApiCampaignReportItem[];
};

export type ApiResponseAvailableFilters = {
  campaign_id: number;
  available_filters: ApiFilterItem[];
};

export type ApiResponseAvailableFiltersCount = {
  campaign_id: number;
  count: number;
};

export const toUpdateCustomItemsApiPayload = (modelObject, proxyParameters) => {
  return {
    id: modelObject.id,
    name: modelObject.name,
    items: [
      {
        id: modelObject.id,
        name: modelObject.name,
        owners_ids: modelObject.owners.map((p) => p.id),
        viewers_ids: modelObject.viewers.map((p) => p.id),
        status: modelObject.status,
        config: {
          filters: toApiFilter(modelObject.filters),
        },
      },
    ],
    ...proxyParameters,
  };
};

export const toCampaignReportCustomItem = (
  data: ApiResponseReport,
  suggestions,
  selectedSuggestionConverter
): CampaignReportItem => {
  return {
    ...data.items[0],
    filters: fromApiFilter(
      suggestions,
      selectedSuggestionConverter,
      data.items[0].config.filters
    ),
  };
};

const formatDemographicsSliceValue = (sliceValue: string) => {
  return startCase(sliceValue.replaceAll('_', ' '));
};

const formatLeadersSliceValue = (
  sliceValue: string,
  formatMessage: IntlShape['formatMessage']
) => {
  const [, level] = sliceValue.split('_');

  const message = formatMessage(
    {
      id: 'app.model.filters.level',
      defaultMessage: 'Level {level}',
    },
    { level }
  );

  if (level === '1') {
    return `${message} (${formatMessage({
      id: 'app.model.filters.level.top_level',
      defaultMessage: 'Top level',
    })})`;
  }

  return message;
};

export const formatCampaignReportDescriptor = (
  report: ApiCampaignReport,
  formatMessage: IntlShape['formatMessage'],
  filterToText?: any
): {
  title: string;
  description: string | JSX.Element | (string | JSX.Element)[];
} => {
  switch (report.sub_type) {
    case CampaignReportSubType.ES_ADMINS:
      return {
        title: formatMessage({
          id: 'app.views.administration.campaign_reports.sub_type.es_admins.title',
          defaultMessage: 'Admin report',
        }),
        description:
          report?.extra_info?.custom_description ??
          formatMessage({
            id: 'app.views.administration.campaign_reports.sub_type.es_admins.description',
            defaultMessage:
              'Report about everyone in the organization available for engagement survey admins.',
          }),
      };
    case CampaignReportSubType.USER_DEFINED:
      switch (report.config.slice_type) {
        case 'DEMOGRAPHIC':
          return {
            title: formatMessage({
              id: 'app.views.administration.campaign_reports.sub_type.user_defined.demographic.title',
              defaultMessage: 'Demographic report',
            }),
            description: (
              <FormattedMessage
                id="app.views.administration.campaign_reports.sub_type.user_defined.demographic.description"
                defaultMessage="Vertical sections of the organizations sliced by <b>{value}</b>"
                values={{
                  value: formatDemographicsSliceValue(report.config.slice_id!),
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            ),
          };
        case 'LEADERS':
          return {
            title: formatMessage({
              id: 'app.views.administration.campaign_reports.sub_type.user_defined.leaders.title',
              defaultMessage: 'Leaders report',
            }),
            description: (
              <FormattedMessage
                id="app.views.administration.campaign_reports.sub_type.user_defined.leaders.description"
                defaultMessage="Reports for <b>{value}</b> employees"
                values={{
                  value: formatLeadersSliceValue(
                    report.config.slice_id!,
                    formatMessage
                  ),
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            ),
          };
        case 'CUSTOM': {
          return {
            title: formatMessage({
              id: 'app.views.administration.campaign_reports.sub_type.user_defined.custom.title',
              defaultMessage: 'Custom report',
            }),
            description: formatMessage(
              {
                id: 'app.views.administration.campaign_reports.sub_type.user_defined.custom.description',
                defaultMessage: '{name} - {filters}',
              },
              {
                name: report.extra_info.custom_name,
                filters: filterToText
                  ? filterToText(report.extra_info.filters)
                  : '',
              }
            ),
          };
        }
      }
      throw new Error('Unknown combination provided');
  }
};

export const formatCampaignReportStatus = (
  report: ApiCampaignReport,
  formatMessage: IntlShape['formatMessage']
): string => {
  switch (report.extra_info.status) {
    case CampaignReportStatus.PUBLISHED:
      return formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey_reports.published',
        defaultMessage: 'Published',
      });
    case CampaignReportStatus.UNPUBLISHED:
      return formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey_reports.unpublished',
        defaultMessage: 'Unpublished',
      });
    case CampaignReportStatus.PARTIALLY_PUBLISHED:
      return formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey_reports.partially_published',
        defaultMessage: 'Partially Published',
      });
  }
};

export const processApiSliceValues = (data: any) => {
  return {
    ...data,
    values: data.values.map((it) => ({
      ...it,
      readOnly: !it.reach_minimum_threshold,
    })),
  };
};

export const toUpdateItemsApiPayload = (modelObject, proxyParameters) => {
  return {
    items: modelObject.items.map((item) => ({
      id: item.id,
      owners_ids: item.owners.map((p) => p.id),
      viewers_ids: item.viewers.map((p) => p.id),
      status: item.status,
    })),
    ...proxyParameters,
  };
};

export const formatPeopleListForSorting =
  (fieldName, propertyName) => (aItem, bItem) => {
    const a = aItem[fieldName];
    const b = bItem[fieldName];
    const diffInLength = a.length - b.length;
    if (diffInLength !== 0) return diffInLength;
    if (a.length === 0) return 0;
    return a[0][propertyName].localeCompare(b[0][propertyName]);
  };
